import { createSelector } from 'reselect';
import _ from 'lodash';

import common from '@common';

const {
  utils: {
    messenger: {
      helpers: { filterRecipients },
    },
  },
} = common;

const getFromMessenger = (state, path) => _.get(state, `internalStore.messenger.${path}`, null);

const getFromMessengerCore = (state, path, defaultValue = null) =>
  _.get(state, `messenger.${path}`, defaultValue);

/**
 * @return {String}
 */
export const getViewMode = state => getFromMessenger(state, 'viewMode');

/**
 * @return {String}
 */
export const getConversationListMode = state =>
  getFromMessenger(state, 'conversationList.listMode');

/**
 * @return {Bool}
 */
export const displayContactDetails = state => getFromMessenger(state, 'displayContactDetails');

/**
 * @return {String}
 */
export const dynamicPersonColors = state => getFromMessenger(state, 'dynamicPersonColors');

/**
 * @return {Boolean}
 */
export const getNewMessageItemVisibility = state =>
  getFromMessenger(state, 'conversationList.newMessageItemVisible');

/**
 * @return {String|Number|null}
 */
export const getSelectedConversationListActiveItem = state =>
  getFromMessenger(state, 'conversationList.activeItem');

/**
 * @return {String|Object|null}
 */
export const getError = state => getFromMessengerCore(state, 'messageError');

/**
 * @return {Array.<Number>}
 */
export const getSelectedConversationIds = state =>
  getFromMessenger(state, 'conversationList.selectedConversationIds');

/**
 * @return {String|null}
 */
export const getSearchQuery = state => getFromMessenger(state, 'conversationSearch.query');

/**
 * @return {Array.<Number>}
 */
export const getSelectedRecipientsList = state =>
  getFromMessenger(state, 'recipients.selectedRecipients');

/**
 * @return {Array.<Object>}
 */
export const getRecipients = state => getFromMessenger(state, 'recipients.recipientsList');

/**
 * @return {Array.<Object>}
 */
export const getRecipientsPhoneNumbers = state =>
  getFromMessenger(state, 'recipients.selectedRecipientsWithPhoneNumbers');

/**
 * @return {Array.<String>}
 */
export const getNewRecipientsList = state => getFromMessenger(state, 'recipients.newRecipients');

/**
 * @return {Array.<Object>}
 */
export const getConversations = state => getFromMessengerCore(state, 'conversations', []);

/**
 * @return {Boolean}
 */
export const getConversationsFetchingFlag = state =>
  getFromMessenger(state, 'conversationList.isFetching');

/**
 * @return {Boolean}
 */
export const getHasNextPageFlag = state => getFromMessenger(state, 'conversationList.hasNextPage');

/**
 * @return {Array.<Object>}
 */
export const getFilteredRecipients = createSelector(
  getRecipients,
  getSelectedRecipientsList,
  (recipients, selectedRecipients) => {
    if (selectedRecipients) {
      return filterRecipients(recipients, selectedRecipients);
    }

    return [];
  },
);

/**
 * @return {Boolean}
 */
export const isSearchInUse = createSelector(
  getSearchQuery,
  searchQuery => _.size(searchQuery) > 0,
);

/**
 * @return {Boolean}
 */
export const getConversationFetchingFlag = state =>
  getFromMessenger(state, 'conversationThread.isFetching');

/**
 * @return {Number|null}
 */
export const getActiveConversationId = state =>
  getFromMessenger(state, 'conversationThread.activeConversationId');

/**
 * @return {Number|null}
 */
export const getActiveContactId = state =>
  getFromMessenger(state, 'conversationThread.activeContactId');

/**
 * @return {Boolean}
 */
export const getSendingMessageFlag = state =>
  getFromMessenger(state, 'conversationThread.isSending');

/**
 * @return {Boolean}
 */
export const getSendingNewMessageFlag = state =>
  getFromMessenger(state, 'conversationThread.isNewSending');

/**
 * @return {Array.<Object>}
 */
export const getConversationThreadMessages = state => {
  const conversationId = getSelectedConversationListActiveItem(state);

  return getFromMessengerCore(state, `conversationsMessages.${conversationId}`, []);
};

/**
 * @return {Object}
 */
export const getActiveConversation = state => getFromMessengerCore(state, `conversation`, {});
export const getActiveContactFromMessages = state => getFromMessengerCore(state, `contact`, null);

export const getActiveContact = state => {
  const contactObject = getActiveContactFromMessages(state);
  const conversationObject = getActiveConversation(state);
  const displayName = _.get(conversationObject, 'display', '');

  if (!_.isEmpty(_.get(contactObject, 'contact'))) {
    return {
      ...contactObject.contact,
      full_name: displayName,
      type_contact: contactObject.type_contact,
    };
  } else if (!_.isEmpty(_.get(contactObject, 'reservation_guest'))) {
    return {
      ...contactObject.reservation_guest,
      full_name: displayName,
      type_contact: contactObject.type_contact,
    };
  } else if (!_.get(contactObject, 'display')) {
    return null;
  }

  return {
    full_name: displayName,
    type_contact: {
      code: 'guest',
      name: 'Guest',
    },
  };
};

export const getReservationForActiveConversation = state => {
  const activeConversationId = getActiveConversationId(state);

  return getFromMessengerCore(state, `reservation.${activeConversationId}`, null);
};

/**
 * @return {Array.<Object>}
 */
export const getContactsForUpdatePhoneNumber = state =>
  getFromMessenger(state, 'recipients.recipientsToUpdate');
